// React
import { useState, useCallback, useEffect } from "react"

// UI
import { cn } from "@/lib/utils"
import { Disclaimer } from "@/components/Disclaimer"
import { Heading } from "@/components/Typography"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"

// I18n
import { useLang } from "@/context/lang"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Types
import { ProjectTypeEnum } from "@/api/graphql/types"

// Constants
import {
	ProjectCrowdfundingTypes,
	endDateStatistics,
} from "@/constants/constants"

// Query
import { usePlatformStatsPerProjectTypeQuery } from "@/api/graphql"

// I18n
import { useTrans } from "@/i18n"

interface FundedAmountPieChartProps {
	setSelectedProjectType: (projectType: ProjectTypeEnum) => void
}

interface ChartDataItem {
	name: string
	value: number
	projectType?: ProjectTypeEnum
	color: string
	isCrowdfunding?: boolean
}

/**
 * FundedAmountPieChart
 * Shows a pie chart of funded amount by category
 */
export function FundedAmountPieChart({
	setSelectedProjectType,
}: FundedAmountPieChartProps) {
	// Dates
	const DateTime = useLocalizedDateTime()

	// Translate
	const t = useTrans("dashboard")
	const { formatCurrency } = useLang()

	// State
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
	const [chartSize, setChartSize] = useState({
		outerRadius: 80,
		innerRadius: 50,
	})
	const [containerKey, setContainerKey] = useState(0)

	// Responsive chart sizing
	useEffect(() => {
		let timeoutId: NodeJS.Timeout

		const updateChartSize = () => {
			clearTimeout(timeoutId)
			timeoutId = setTimeout(() => {
				const width = window.innerWidth
				let newSize
				if (width < 640) {
					// sm breakpoint
					newSize = { outerRadius: 60, innerRadius: 35 }
				} else if (width < 1024) {
					// lg breakpoint
					newSize = { outerRadius: 80, innerRadius: 50 }
				} else {
					newSize = { outerRadius: 120, innerRadius: 75 }
				}

				setChartSize(newSize)
				// Force ResponsiveContainer to re-render
				setContainerKey((prev) => prev + 1)
			}, 150)
		}

		// Initial size calculation
		const width = window.innerWidth
		let initialSize
		if (width < 640) {
			initialSize = { outerRadius: 60, innerRadius: 35 }
		} else if (width < 1024) {
			initialSize = { outerRadius: 80, innerRadius: 50 }
		} else {
			initialSize = { outerRadius: 120, innerRadius: 75 }
		}
		setChartSize(initialSize)

		window.addEventListener("resize", updateChartSize)
		return () => {
			window.removeEventListener("resize", updateChartSize)
			clearTimeout(timeoutId)
		}
	}, [])

	// Query
	const { data: queryData, isLoading } = usePlatformStatsPerProjectTypeQuery({
		date: endDateStatistics,
	})

	// Chart colors using the specified brand palette
	const chartColors = {
		crowdfunding: "#62a7ba", // Crowdfunding (includes portfolio, bonds, shares)
		portfolio: "#62a7ba", // Same as crowdfunding parent
		solarBonds: "#62a7ba", // Same as crowdfunding parent
		solarShares: "#62a7ba", // Same as crowdfunding parent
		solarFunds: "#efb356", // Zonfonds
		solarLease: "#cc6022", // Zonlease
	}

	// Transform GraphQL data to chart format - simplified to 3 pieces
	const rawData = queryData?.platform_statistics_per_project_type ?? []

	// Create simplified data structure with only 3 pieces
	const data: ChartDataItem[] = []

	// Calculate total crowdfunding amount
	let totalCrowdfunding = 0
	const crowdfundingTypes = rawData.filter(
		(item) =>
			item?.type &&
			ProjectCrowdfundingTypes.includes(item.type as ProjectTypeEnum),
	)
	crowdfundingTypes.forEach((item) => {
		if (item) {
			totalCrowdfunding += item.results?.total_amount_invested ?? 0
		}
	})

	// Add crowdfunding as single combined piece
	if (totalCrowdfunding > 0) {
		data.push({
			name: t("dashboard.funded_amount_pie_chart.crowdfunding"),
			value: totalCrowdfunding,
			color: chartColors.crowdfunding,
			isCrowdfunding: true,
		})
	}

	// Add non-crowdfunding items directly
	rawData.forEach((item) => {
		if (
			!item?.type ||
			ProjectCrowdfundingTypes.includes(item.type as ProjectTypeEnum)
		)
			return

		const chartItem: ChartDataItem = {
			name: t(`common:common.project.type.${item.type}`),
			value: item.results!.total_amount_invested ?? 0,
			projectType: item.type as ProjectTypeEnum,
			color: "",
		}

		// Assign colors based on project type
		switch (item.type) {
			case ProjectTypeEnum.SolarFunds:
				chartItem.color = chartColors.solarFunds
				break
			case ProjectTypeEnum.SolarLease:
				chartItem.color = chartColors.solarLease
				break
		}

		data.push(chartItem)
	})

	// Calculate total funded amount
	const totalFunded = data.reduce((sum, item) => sum + item.value, 0)

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload
			return (
				<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
					<p className="font-medium text-gray-900">{data.name}</p>
					<p className="text-sm text-gray-600">
						{formatCurrency(data.value, {
							maximumFractionDigits: 0,
						})}
					</p>
				</div>
			)
		}
		return null
	}

	const handleSliceClick = useCallback(
		(entry: ChartDataItem) => {
			if (entry.projectType) {
				setSelectedProjectType(entry.projectType)
			} else if (entry.isCrowdfunding) {
				setSelectedProjectType(ProjectTypeEnum.SolarShares)
			}
		},
		[setSelectedProjectType],
	)

	// Handle loading state
	if (isLoading) {
		return (
			<div className="mb-8 w-full">
				<div className="mb-6 text-center">
					<div className="mb-4">
						<div className="mx-auto h-8 w-48 animate-pulse rounded bg-gray-200"></div>
						<div className="text-lg text-gray-600">
							{t(
								"dashboard.funded_amount_pie_chart.total_financed_via_zonhub",
							)}
						</div>
					</div>
				</div>
				<div className="w-full rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
					<Heading as="h3" className="mb-2 text-gray-900">
						{t(
							"dashboard.funded_amount_pie_chart.funded_amount_by_category",
						)}
					</Heading>
					<p className="mb-4 text-sm text-gray-600">
						{t("dashboard.statistics.platform.as_per_date", {
							date: DateTime.fromJSDate(
								endDateStatistics,
							).toLocaleString(),
						})}
					</p>
					<div className="flex flex-col gap-4 sm:gap-8 xl:flex-row">
						{/* Chart Loading */}
						<div className="w-full min-w-0 overflow-hidden xl:flex-1">
							<div className="flex h-64 items-center justify-center sm:h-80 md:h-96 xl:h-[400px]">
								<div className="size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
							</div>
						</div>

						{/* Legend Loading */}
						<div className="w-full space-y-2 overflow-hidden xl:w-80 xl:shrink-0">
							{[1, 2, 3].map((index) => (
								<div
									key={`loading-legend-${index}`}
									className="flex items-center gap-3 rounded-lg bg-white p-3"
								>
									<div className="size-4 shrink-0 animate-pulse rounded-full bg-gray-200" />
									<div className="min-w-0 flex-1 overflow-hidden">
										<div className="mb-1 h-4 animate-pulse rounded bg-gray-200" />
										<div className="h-3 w-2/3 animate-pulse rounded bg-gray-200" />
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Handle no data
	if (!data || data.length === 0) {
		return (
			<div className="mb-8">
				<div className="mb-6 text-center">
					<div className="mb-4">
						<div className="text-3xl font-bold text-gray-900">
							{formatCurrency(0)}
						</div>
						<div className="text-lg text-gray-600">
							{t(
								"dashboard.funded_amount_pie_chart.total_financed_via_zonhub",
							)}
						</div>
					</div>
					<Heading as="h3" className="mb-2 text-gray-900">
						{t(
							"dashboard.funded_amount_pie_chart.funded_amount_by_category",
						)}
					</Heading>
					<p className="mb-4 text-sm text-gray-600">
						{t("dashboard.statistics.platform.as_per_date", {
							date: DateTime.fromJSDate(
								endDateStatistics,
							).toLocaleString(),
						})}
					</p>
				</div>
				<div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
					<div className="py-12 text-center text-gray-500">
						{t("dashboard.funded_amount_pie_chart.no_funding_data")}
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="mb-8 w-full">
			<div className="mb-6 text-center">
				<div className="mb-4">
					<div className="text-3xl font-bold text-gray-900">
						{formatCurrency(totalFunded, {
							maximumFractionDigits: 0,
						})}
					</div>
					<div className="text-lg text-gray-600">
						{t(
							"dashboard.funded_amount_pie_chart.total_financed_via_zonhub",
						)}
					</div>
				</div>
			</div>

			{/** Disclaimer */}
			<Disclaimer
				className="mb-4"
				title={t(
					"dashboard:dashboard.stats.platform_stats.disclaimer.title",
				)}
				message={t(
					"dashboard:dashboard.stats.platform_stats.disclaimer.text",
				)}
			/>

			<div className="w-full rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
				<Heading as="h3" className="mb-2 text-gray-900">
					{t(
						"dashboard.funded_amount_pie_chart.funded_amount_by_category",
					)}
				</Heading>
				<p className="mb-4 text-sm text-gray-600">
					{t("dashboard.statistics.platform.as_per_date", {
						date: DateTime.fromJSDate(
							endDateStatistics,
						).toLocaleString(),
					})}
				</p>
				<div className="flex flex-col gap-4 sm:gap-8 xl:flex-row">
					{/* Chart */}
					<div className="w-full min-w-0 overflow-hidden xl:flex-1">
						<ResponsiveContainer
							key={containerKey}
							width="100%"
							height={300}
							className="sm:h-80 md:h-96 xl:h-[400px]"
						>
							<PieChart>
								<Pie
									data={data}
									cx="50%"
									cy="50%"
									labelLine={false}
									outerRadius={chartSize.outerRadius}
									innerRadius={chartSize.innerRadius}
									startAngle={90}
									endAngle={450}
									fill="#8884d8"
									dataKey="value"
									onMouseEnter={(_, index) =>
										setHoveredIndex(index)
									}
									onMouseLeave={() => setHoveredIndex(null)}
									animationBegin={0}
									animationDuration={800}
								>
									{data.map((entry, index) => {
										const isHovered = hoveredIndex === index

										return (
											<Cell
												key={`cell-${index}`}
												fill={entry.color}
												stroke={
													isHovered
														? "#ffffff"
														: "transparent"
												}
												strokeWidth={isHovered ? 2 : 0}
												onClick={() =>
													handleSliceClick(entry)
												}
												className={cn(
													"cursor-pointer transition-all duration-300 focus:outline-none",
													isHovered &&
														"drop-shadow-lg",
												)}
												style={{
													filter: isHovered
														? "brightness(1.1)"
														: "brightness(1)",
													strokeOpacity: isHovered
														? 0.9
														: 0,
													strokeLinejoin: "round",
													outline: "none",
												}}
											/>
										)
									})}
								</Pie>
								<Tooltip content={<CustomTooltip />} />
							</PieChart>
						</ResponsiveContainer>
					</div>

					{/* Legend */}
					<div className="w-full space-y-2 overflow-hidden xl:w-80 xl:shrink-0">
						{data.map((entry, index) => {
							const percentage = (
								(entry.value / totalFunded) *
								100
							).toFixed(1)

							return (
								<button
									type="button"
									key={`legend-${entry.name}-${index}`}
									onClick={() => handleSliceClick(entry)}
									className="flex w-full cursor-pointer items-center gap-3 rounded-lg bg-white p-3 text-left transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
									aria-label={`Filter by ${entry.name}, ${formatCurrency(entry.value, { maximumFractionDigits: 0 })}, ${percentage} percent`}
								>
									<div
										className="size-4 shrink-0 rounded-full"
										style={{
											backgroundColor: entry.color,
										}}
										aria-hidden="true"
									/>
									<div className="min-w-0 flex-1 overflow-hidden">
										<div className="truncate font-medium text-gray-900">
											{entry.name}
										</div>
										<div className="truncate text-sm text-gray-500">
											{formatCurrency(entry.value, {
												maximumFractionDigits: 0,
											})}{" "}
											• {percentage}%
										</div>
									</div>
								</button>
							)
						})}
					</div>
				</div>
			</div>
		</div>
	)
}
